import { AxiosResponse } from 'axios';
import { doorClient, leyanbotClient, ImResponse, resolve } from 'services/api';

type PageResponse<T> = {
  page: Knowledge.Pagination & { total: number };
  data: T;
};

function pageResponseResolve<T>(promise: Promise<AxiosResponse<PageResponse<T>>>) {
  return promise.then((response) => {
    return response.data;
  });
}

export const queryByKnowledge = (data: { knowledgeName: string; page: Knowledge.Pagination }) => {
  return pageResponseResolve(
    doorClient.post<PageResponse<Knowledge.SPU.Knowledge[]>>(
      '/knowledge/spu_knowledges/v2/query_by_knowledge',
      data,
    ),
  );
};

export const queryBySpu = (data: {
  spus: Partial<Knowledge.SPU.SpuInfo>[];
  page: Knowledge.Pagination;
  keywordType: Knowledge.SPU.SpuKeywordType;
  searchKeyword: string;
}) => {
  return pageResponseResolve(
    doorClient.post<PageResponse<Knowledge.SPU.SpuWithKnowledges[]>>(
      '/knowledge/spu_knowledges/v2/query_by_spu',
      data,
    ),
  );
};

export const createKnowledge = (data: { spuKnowledge: Knowledge.SPU.KnowledgeFormValue }) => {
  return resolve<unknown>(doorClient.post('/knowledge/spu_knowledges/v2/create', data));
};

export const updateKnowledge = (data: { spuKnowledge: Knowledge.SPU.Knowledge }) => {
  return resolve<unknown>(doorClient.post('/knowledge/spu_knowledges/v2/update', data));
};

export const updateKnowledgeBySpu = (data: { spuKnowledge: Knowledge.SPU.Knowledge }) => {
  return resolve<unknown>(doorClient.post('/knowledge/spu_knowledges/v2/update_by_spu', data));
};

export const deleteKnowledge = (data: { knowledgeId: number }) => {
  return resolve<unknown>(doorClient.post('/knowledge/spu_knowledges/delete', data));
};

export const deleteSpuMapping = (data: { knowledgeId: number; spu: Knowledge.SPU.SpuInfo }) => {
  return resolve<unknown>(doorClient.post('/knowledge/spu_knowledges/delete_spu_mapping', data));
};

export const updateKnowledgeV3 = (data: { spuKnowledge: Knowledge.SPU.Knowledge }) => {
  return resolve<unknown>(doorClient.post('/knowledge/spu_knowledges/v3/update', data));
};

/************************* 标签管理 start **************************/
type TagSpuVO = {
  id: number;
  externalItemId: string;
  itemName: string;
  imageUrl: string;
  skus: { skuId: string; skuName: string }[];
};
type TagVO = {
  id: number;
  name: string;
  spuCount: number;
  createdAt: number;
  updatedAt: number;
};
type TagDetailVO = TagVO & {
  spuList: TagSpuVO[];
};
type TagListResponse = {
  list: TagVO[];
  page: {
    pageNo: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
};

export const defaultTagResponse: () => TagListResponse = () => {
  return {
    list: [],
    page: {
      pageSize: 10,
      pageNo: 1,
      totalCount: 0,
      totalPages: 0,
    },
  };
};

// 列表
export const querySpuLabel = (data: {
  tagName: string;
  pageInfo: {
    pageSize: number;
    pageNo: number;
  };
}) => {
  return resolve(
    leyanbotClient.get<ImResponse<TagListResponse>>('/overseaim-item-service/api/v1/tags', {
      params: {
        keyword: data.tagName || undefined,
        pageNo: data.pageInfo.pageNo,
        pageSize: data.pageInfo.pageSize,
      },
    }),
  );
};

// 新增
export const createSpuLabel = (data: {
  tagName: string;
  operateType?: 1 | 2;
  tagIndexData: Knowledge.SPU.TagIndexData[];
}) => {
  return leyanbotClient.post<ImResponse<TagVO>>('/overseaim-item-service/api/v1/tags', {
    name: data.tagName,
    operateType: data.operateType,
    spuList: data.tagIndexData.map((item) => ({
      externalItemId: item.spuId,
      externalStoreId: item.externalStoreId,
      externalType: item.externalType,
      region: item.region,
      skuIds: item.skuId || [],
    })),
  });
};

// 更新
export const updateSpuLabel = (data: {
  tagId: string;
  tagName?: string;
  operateType: 1 | 2;
  tagIndexData?: Knowledge.SPU.TagIndexData[];
}) => {
  return leyanbotClient.put<ImResponse<TagVO>>(
    `/overseaim-item-service/api/v1/tags/${data.tagId}`,
    {
      name: data.tagName,
      operateType: data.operateType,
      spuList: data.tagIndexData?.map((item) => ({
        externalItemId: item.spuId,
        externalStoreId: item.externalStoreId,
        externalType: item.externalType,
        region: item.region,
        skuIds: item.skuId || [],
      })),
    },
  );
};

// 删除
export const deleteSpuLabel = (data: { tagId: string }) => {
  return leyanbotClient.delete(`/overseaim-item-service/api/v1/tags/${data.tagId}`);
};

// 获取标签详情，也可用于判断标签是否存在
export const getTag = (data: { tagId?: string; tagName?: string }) => {
  if (data.tagId) {
    return resolve(
      leyanbotClient.get<ImResponse<TagDetailVO>>(
        `/overseaim-item-service/api/v1/tags/${data.tagId}`,
        { params: { pageNo: 1, pageSize: 1000 } },
      ),
    );
  }
  // 按名称查标签（用于重复检查）
  // 列表接口是模糊搜索，需要精确匹配后再返回
  return resolve(
    leyanbotClient.get<ImResponse<TagListResponse>>('/overseaim-item-service/api/v1/tags', {
      params: { keyword: data.tagName, pageNo: 1, pageSize: 50 },
    }),
  ).then((res) => {
    const exact = res.list?.find((item) => item.name === data.tagName);
    return (exact || null) as unknown as TagDetailVO;
  });
};
/************************* 标签管理 end **************************/
